import { useEffect, useState, useCallback } from "react";
import { apiFetch, apiGetJson } from "../api/client";

function toIdSet(ids) {
  return new Set((ids || []).map((x) => String(x)));
}

export function useRequestStatuses() {
  const [pendingListingIds, setPendingListingIds] = useState(() => new Set());
  const [approvedListingIds, setApprovedListingIds] = useState(() => new Set());
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      setError("");

      // Active (pending)
      const activeRes = await apiFetch("/material_management/requests/active", {
        method: "GET",
      });

      if (activeRes.ok) {
        try {
          const activeData = await activeRes.json();
          setPendingListingIds(toIdSet(activeData?.listing_ids));
        } catch (parseError) {
          // response was not valid JSON, skip setting pending IDs
          console.warn("Failed to parse active requests response:", parseError);
        }
      }

      // Approved
      const approvedData = await apiGetJson("/material_management/requests/approved").catch(
        () => null
      );

      if (approvedData) {
        if (Array.isArray(approvedData?.requests)) {
          const ids = approvedData.requests
            .map((r) => String(r?.listing_id ?? ""))
            .filter(Boolean);
          setApprovedListingIds(new Set(ids));
        } else {
          setApprovedListingIds(toIdSet(approvedData?.listing_ids));
        }
      }
    } catch (e) {
      setError(e?.message || "Failed to load request statuses");
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await refresh();
    })();

    return () => {
      alive = false;
    };
  }, [refresh]);

  const markPendingOptimistic = useCallback((listingId) => {
    const lid = String(listingId);

    setPendingListingIds((prev) => {
      const next = new Set(prev);
      next.add(lid);
      return next;
    });

    setApprovedListingIds((prev) => {
      const next = new Set(prev);
      next.delete(lid);
      return next;
    });
  }, []);

  const unmarkPending = useCallback((listingId) => {
    const lid = String(listingId);
    setPendingListingIds((prev) => {
      const next = new Set(prev);
      next.delete(lid);
      return next;
    });
  }, []);

  return {
    pendingListingIds,
    approvedListingIds,
    refresh,
    error,
    markPendingOptimistic,
    unmarkPending,
  };
}
